import React from "react";
import styled from "styled-components";
import { SectionH2, GradientWord, Eyebrow } from "../components/Headlines";
import { FaPenNib, FaCode, FaSyncAlt } from "react-icons/fa";

const StyledServices = styled.section`
  background-color: ${(props) => props.theme.darkBg2};
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 100px 30px;

  @media screen and (min-width: 800px) {
    padding: 120px 60px;
  }
`;

const Header = styled.div`
  text-align: center;
  max-width: 720px;
  margin-bottom: 56px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 18px;
`;

const Subhead = styled.p`
  font-family: ${(props) => props.theme.lightFont};
  font-size: 18px;
  line-height: 1.6;
  color: ${(props) => props.theme.textMuted};
  margin: 0;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
  width: 100%;
  max-width: 1080px;

  @media screen and (min-width: 900px) {
    grid-template-columns: repeat(3, 1fr);
    align-items: stretch;
  }
`;

const Card = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
  padding: 36px 30px;
  background: ${(props) => props.theme.panel};
  border: 1px solid
    ${(props) =>
      props.$featured ? props.theme.accentBorder : props.theme.hairline};
  border-radius: 20px;
  position: relative;

  ${(props) =>
    props.$featured &&
    `box-shadow: 0 0 0 1px ${props.theme.accentBorder}, 0 20px 60px -30px ${props.theme.accent};`}
`;

const IconWrap = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 12px;
  background: ${(props) => props.theme.accentSoft};
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 22px;
    height: 22px;
    color: ${(props) => props.theme.accent};
  }
`;

const CardTitle = styled.h3`
  font-family: ${(props) => props.theme.regularFont};
  font-size: 22px;
  font-weight: 600;
  color: ${(props) => props.theme.textColor};
  margin: 0;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Tag = styled.span`
  font-family: ${(props) => props.theme.regularFont};
  font-size: 10px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: white;
  background: ${(props) => props.theme.accent};
  padding: 4px 9px;
  border-radius: 20px;
`;

const CardText = styled.p`
  font-family: ${(props) => props.theme.lightFont};
  font-size: 15px;
  line-height: 1.6;
  color: ${(props) => props.theme.textMuted};
  margin: 0;
`;

const services = [
  {
    icon: FaPenNib,
    title: "Design",
    text: "Conversion-first design that makes a complex product clear. Pages buyers actually move through - not just pretty mockups.",
  },
  {
    icon: FaCode,
    title: "Build",
    text: "Webflow, WordPress, fully custom - everything supported. Fast, accessible, secure builds on whatever fits your team. No platform lock-in.",
  },
  {
    icon: FaSyncAlt,
    title: "Maintain",
    featured: true,
    text: "The part most agencies skip. Same-day changes, ongoing SEO, GEO and conversion work, and someone who always knows your site.",
  },
];

export default function Services() {
  return (
    <StyledServices id="services">
      <Header>
        <Eyebrow>What we offer</Eyebrow>
        <SectionH2>
          Design, build, and the part that <GradientWord>never ends</GradientWord>
        </SectionH2>
        <Subhead>
          One in-house team for all three. No handoffs between a design shop, a
          dev shop, and whoever's left to keep it running.
        </Subhead>
      </Header>
      <Grid>
        {services.map((s) => {
          const Icon = s.icon;
          return (
            <Card key={s.title} $featured={s.featured}>
              <IconWrap>
                <Icon />
              </IconWrap>
              <CardTitle>
                {s.title}
                {s.featured && <Tag>Where we're different</Tag>}
              </CardTitle>
              <CardText>{s.text}</CardText>
            </Card>
          );
        })}
      </Grid>
    </StyledServices>
  );
}
